import {
  SignatureTemplate,
  useSignatureTemplates,
} from '@commenda/api/docusign';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  Alert,
  Badge,
  Button,
  FieldWrapper,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleCombobox,
} from '@commenda/kakashi';
import { SignatureTemplateStatus } from '@commenda/prisma';
import {
  EsignStepMode,
  TEsignSignerRoleConfig,
  THumanEsignAdHocStepDefinition,
  THumanEsignStepDefinition,
  THumanEsignTemplateStepDefinition,
  TValueProvider,
} from '@commenda/types';
import { FieldRefSource } from '@commenda/types/src/workflow/dsl/enums';
import { useWorkflow } from '../../context/WorkflowContext';
import { WorkflowGraphHelper } from '../../helpers/WorkflowGraphHelper';
import { TSelectedEntity } from '../../types/node';

/**
 * v1 prefill bindings are context paths (TValueProvider field/context). The
 * step definition supports const/expr providers too; richer editors can come
 * later without changing the stored shape.
 */
const contextPathFromProvider = (provider?: TValueProvider): string => {
  if (provider?.type !== 'field') return '';
  if (provider.ref.source !== FieldRefSource.CONTEXT) return '';
  return provider.ref.path;
};

const providerFromContextPath = (path: string): TValueProvider | undefined => {
  const trimmed = path.trim();
  if (!trimmed) return undefined;
  return {
    type: 'field',
    ref: { source: FieldRefSource.CONTEXT, path: trimmed },
  };
};

export const EsignNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();
  const { templates, isLoading } = useSignatureTemplates(
    SignatureTemplateStatus.PUBLISHED
  );

  const step = workflow?.steps.find(
    (candidate) => candidate.id === selectedEntity.id
  ) as THumanEsignStepDefinition;

  if (!workflow) return <div>Workflow not found</div>;
  if (!step) return <div>Step not found</div>;

  const adHocLaunched = isFeatureLaunched(FeatureLaunch.ESIGN_AD_HOC_DOCUMENT);
  // Absent mode is TEMPLATE: every step authored before ad-hoc mode existed omits it. Where the
  // feature is not launched the panel authors template steps only, so a step promoted in from an
  // environment that has it still reads correctly rather than showing a mode that cannot be changed.
  const mode = step.mode ?? EsignStepMode.TEMPLATE;
  const selectedStep = step as THumanEsignTemplateStepDefinition;
  const adHocStep = step as THumanEsignAdHocStepDefinition;

  const upstreamAgentStepOptions = WorkflowGraphHelper.findUpstreamAgentSteps(
    workflow,
    selectedEntity.id
  ).map((agentStep) => ({ label: agentStep.name, value: agentStep.id }));

  const templateOptions = (templates ?? []).map((template) => ({
    label: template.name,
    value: template.id,
  }));

  const selectedTemplate = templates?.find(
    (template) => template.id === selectedStep.signatureTemplateId
  );

  const updateEsignStep = (
    updates:
      | Partial<THumanEsignTemplateStepDefinition>
      | Partial<THumanEsignAdHocStepDefinition>
  ) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((candidate) =>
        candidate.id === selectedEntity.id
          ? { ...candidate, ...updates }
          : candidate
      ),
    });
  };

  /** Switching mode drops the other mode's config outright — a half-configured step fails publish validation. */
  const onSelectMode = (nextMode: EsignStepMode) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((candidate) => {
        if (candidate.id !== selectedEntity.id) return candidate;

        const base = { ...candidate } as Record<string, unknown>;
        delete base.signatureTemplateId;
        delete base.signerRoles;
        delete base.documentSourceStepId;

        return { ...base, mode: nextMode } as typeof candidate;
      }),
    });
  };

  /** Mirror the template's roles onto the step, preserving prefill bindings for roles that still exist. */
  const rolesFromTemplate = (
    template: SignatureTemplate
  ): TEsignSignerRoleConfig[] => {
    const existingByRoleName = new Map(
      (selectedStep.signerRoles ?? []).map((role) => [role.roleName, role])
    );

    return template.Roles.map((templateRole) => {
      const existing = existingByRoleName.get(templateRole.roleName);
      return {
        roleName: templateRole.roleName,
        displayName: existing?.displayName ?? templateRole.displayName,
        ...(existing?.prefill ? { prefill: existing.prefill } : {}),
      };
    });
  };

  const onSelectTemplate = (templateId: string) => {
    const template = templates?.find((t) => t.id === templateId);
    updateEsignStep({
      signatureTemplateId: templateId,
      signerRoles: template ? rolesFromTemplate(template) : [],
    });
  };

  const updateRolePrefill = (
    roleName: string,
    key: 'name' | 'email',
    path: string
  ) => {
    const provider = providerFromContextPath(path);

    updateEsignStep({
      signerRoles: (selectedStep.signerRoles ?? []).map((role) => {
        if (role.roleName !== roleName) return role;

        const prefill = { ...role.prefill };
        if (provider) prefill[key] = provider;
        else delete prefill[key];

        const hasBindings = prefill.name != null || prefill.email != null;
        const { prefill: _dropped, ...rest } = role;
        return hasBindings ? { ...rest, prefill } : rest;
      }),
    });
  };

  // Publish-time validation enforces the exact match; this surfaces drift early.
  const templateRoleNames = selectedTemplate?.Roles.map((r) => r.roleName);
  const stepRoleNames = (selectedStep.signerRoles ?? []).map((r) => r.roleName);
  const rolesOutOfSync =
    selectedTemplate != null &&
    (templateRoleNames?.length !== stepRoleNames.length ||
      templateRoleNames?.some((name) => !stepRoleNames.includes(name)));

  return (
    <div className="flex flex-col gap-4">
      {(adHocLaunched || mode === EsignStepMode.AD_HOC) && (
        <FieldWrapper>
          <Label>Document Source</Label>
          <Select
            value={mode}
            onValueChange={(value) => onSelectMode(value as EsignStepMode)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a document source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EsignStepMode.TEMPLATE}>
                Signing template
              </SelectItem>
              <SelectItem value={EsignStepMode.AD_HOC}>
                Document from this run
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            {mode === EsignStepMode.TEMPLATE
              ? 'Sends a template whose fields and signing order were placed once, ahead of time.'
              : 'Sends a document produced during the run. The assignee names the signers and places the fields on every run.'}
          </p>
        </FieldWrapper>
      )}

      {mode === EsignStepMode.AD_HOC && (
        <FieldWrapper>
          <Label>Document from step (optional)</Label>
          <SingleCombobox
            options={upstreamAgentStepOptions}
            value={adHocStep.documentSourceStepId ?? ''}
            setValue={(value) =>
              updateEsignStep({ documentSourceStepId: value || undefined })
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            {upstreamAgentStepOptions.length === 0
              ? 'No document-generating steps run before this one. The assignee will upload the document themselves.'
              : 'The step whose generated document is offered by default. Leave empty to have the assignee supply it. Only steps that always run before this one are listed.'}
          </p>
        </FieldWrapper>
      )}

      {mode === EsignStepMode.TEMPLATE && (
        <FieldWrapper>
          <Label>Signing Template</Label>
          <SingleCombobox
            options={templateOptions}
            value={selectedStep.signatureTemplateId || ''}
            setValue={onSelectTemplate}
          />
          <p className="text-xs text-gray-500 mt-1">
            {isLoading
              ? 'Loading published templates…'
              : 'Only published templates are listed. Create and map templates under Signing Templates.'}
          </p>
        </FieldWrapper>
      )}

      {mode === EsignStepMode.TEMPLATE && rolesOutOfSync && (
        <Alert
          variant="warning"
          description="The template's roles changed since this step was configured. Refresh to re-sync — prefill bindings for unchanged roles are kept."
        />
      )}
      {mode === EsignStepMode.TEMPLATE &&
        rolesOutOfSync &&
        selectedTemplate && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              updateEsignStep({
                signerRoles: rolesFromTemplate(selectedTemplate),
              })
            }
          >
            <Icons.RefreshCw01 className="size-4" />
            Refresh roles from template
          </Button>
        )}

      {mode === EsignStepMode.TEMPLATE &&
        (selectedStep.signerRoles ?? []).length > 0 && (
          <FieldWrapper>
            <Label>Signer Roles</Label>
            <p className="text-xs text-gray-500 mb-2">
              Actual signers are chosen on each workflow run by the step&apos;s
              assignee. Bind roles to workflow context to pre-fill their
              suggestions. Signing order is set in the template&apos;s field
              mapping editor.
            </p>
            <div className="flex flex-col gap-3">
              {(selectedStep.signerRoles ?? []).map((role) => {
                const templateRole = selectedTemplate?.Roles.find(
                  (r) => r.roleName === role.roleName
                );
                return (
                  <div
                    key={role.roleName}
                    className="flex flex-col gap-2 p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center justify-between">
                      <span className="txt-small-medium">
                        {role.displayName}
                      </span>
                      {templateRole && (
                        <Badge variant="gray" size="sm">
                          Order {templateRole.routingOrder}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs">
                          Pre-fill name from context (optional)
                        </Label>
                        <Input
                          value={contextPathFromProvider(role.prefill?.name)}
                          placeholder="e.g., directors[0].name"
                          onChange={(e) =>
                            updateRolePrefill(
                              role.roleName,
                              'name',
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs">
                          Pre-fill email from context (optional)
                        </Label>
                        <Input
                          value={contextPathFromProvider(role.prefill?.email)}
                          placeholder="e.g., directors[0].email"
                          onChange={(e) =>
                            updateRolePrefill(
                              role.roleName,
                              'email',
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </FieldWrapper>
        )}
    </div>
  );
};
